import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfileStore } from '../../store/useProfileStore';
import { useUpdateProfileEmail } from '../../hooks/useProfile';
import { Modal } from '../ui/Modal';

interface EditEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditEmailModal: React.FC<EditEmailModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('profile');
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const updateEmail = useUpdateProfileEmail();

  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('edit');
      setEmail(activeProfile?.email ?? '');
      setError('');
    }
  }, [isOpen, activeProfile?.email]);

  const trimmed = email.trim();
  const isUnchanged = trimmed === (activeProfile?.email ?? '');
  const isInvalid = !trimmed || !trimmed.includes('@');
  const isContinueDisabled = isUnchanged || isInvalid;

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (isContinueDisabled) return;
    setError('');
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!activeProfile || updateEmail.isPending) return;
    setError('');
    try {
      await updateEmail.mutateAsync({ profileId: activeProfile.id, email: trimmed });
      onClose();
    } catch (err: unknown) {
      const status = (err as Error & { status?: number }).status;
      if (status === 409) {
        setError(t('contact.emailModal.errorConflict'));
      } else {
        setError(t('contact.emailModal.errorGeneric'));
      }
      // Surface the error on the confirm screen — user can Back to fix
      // a typo or retry. We don't auto-rewind to edit so a transient
      // failure doesn't make them retype the address.
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass="z-50">
      {step === 'edit' ? (
          <>
            <h2 className="text-lg font-bold text-foreground mb-2">
              {t('contact.emailModal.title')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('contact.emailModal.body')}
            </p>
            <form onSubmit={handleContinue} className="flex flex-col gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('contact.emailModal.placeholder')}
                required
                autoFocus
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
              {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isContinueDisabled}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                >
                  {t('common:actions.continue')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-foreground mb-2">
              {t('contact.emailModal.confirmTitle')}
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              {t('contact.emailModal.confirmBody')}
            </p>
            <div className="rounded-lg border border-border bg-background px-3 py-2 mb-3">
              <p className="text-xs text-muted-foreground">
                {t('contact.emailLabel')}
              </p>
              <p className="text-sm font-medium text-foreground break-all">
                {trimmed}
              </p>
            </div>
            <div className="rounded-lg border border-brand-danger/40 bg-brand-danger/10 px-3 py-2 mb-4">
              <p className="text-xs text-brand-danger">
                {t('contact.emailModal.confirmWarning')}
              </p>
            </div>
            {error && (
              <p className="text-brand-danger text-sm text-center mb-3">{error}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setStep('edit');
                  setError('');
                }}
                disabled={updateEmail.isPending}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
              >
                {t('common:actions.back')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={updateEmail.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
              >
                {updateEmail.isPending
                  ? t('contact.emailModal.saving')
                  : t('contact.emailModal.confirmCta')}
              </button>
            </div>
          </>
        )}
    </Modal>
  );
};
